import {Col, Container, Row} from 'react-bootstrap';
import styled from 'styled-components';
import {useBenefits} from '../../hooks/useBenefits';
import {IFooterPaymentMethod} from '../../interfaces/IFooterPaymentMethod';
import baseTheme from '../../shared/baseTheme';
import Icon from '../Icon';
import {IBenefit} from "../../interfaces/IBenefit";
import Link from 'next/link';
import {getNavPath} from '../../shared/navigateUtil';
import {useRouter} from 'next/router';
import NewsletterSubscribe from "../NewsletterSubscribe";
import {useMessages} from "../../hooks/useMessages";
import {useContext} from "react";
import {ClientContext} from "../../contexts/ClientContext";
import {IFooterColumn, IFooterLink} from "../../interfaces/IFooterLinks";
import {useFooterData} from "../../hooks/useFooterData";
import {getTheme} from "../../shared/themeUtils";

const FooterContain = styled.footer`
    .column-title {
        font-weight: bold;
        margin-bottom: ${getTheme("space.2")};
    }

    .data-source {
        color: ${getTheme("colors.secondaryFont")};
        font-size: ${getTheme("fontSizes.small")};
        margin-top: ${getTheme("space.3")};
        padding-top: ${getTheme("space.2")};
        border-top: 1px solid rgba(255, 255, 255, 0.1);
    }
`

const FooterUpper = styled.div`
    background-color: ${getTheme("colors.background")};
    padding: ${getTheme("space.3")} 0;
    color: ${getTheme("colors.primary")};

    .columns {
        display: flex;
        flex-direction: row;

        .column-contain {
            width: 33%;
            padding-right: ${getTheme("space.2")};

            ul.benefits {
                padding: 0;
                list-style: none;
                color: ${getTheme("colors.secondaryFont")};
                font-size: ${getTheme("fontSizes.small")};

                li {
                    margin-bottom: ${getTheme("space.2")};
                }
            }
        }

        @media (max-width: ${getTheme("breakpoints.2")}) {
            flex-direction: column;
            align-items: center;
            text-align: center;

            .column-contain {
                margin-bottom: ${getTheme("space.3")};
                width: 100%;
            }
        }
    }

    p {
        color: ${getTheme("colors.secondaryFont")};
        font-size: ${getTheme("fontSizes.large")};
        font-weight: bold;
    }

    .payment-options {
        display: flex;
        flex-wrap: wrap;

        .payment-option {
            width: 100px;
            height: 100px;

            display: flex;
            justify-content: center;
            align-items: center;
            background: #fff;
            margin: ${getTheme("space.0")};
            border-radius: ${getTheme("borderRadius")};

            img {
                max-width: 100%;
                max-height: 60%;
                display: inline-block;
                padding: ${getTheme("space.1")};
            }
        }

        @media (max-width: ${getTheme("breakpoints.2")}) {
            justify-content: center;
        }
    }

    .footer-icon {
        display: inline-flex;
        margin-right: ${getTheme("space.2")};
    }


    @media (max-width: ${getTheme("breakpoints.1")}) {
        text-align: center;

        p {
            margin-top: ${getTheme("space.2")};
            margin-bottom: ${getTheme("space.0")};
        }
    }
`;

const FooterLower = styled.div`
    background-color: ${getTheme("colors.primary")};
    padding: ${getTheme("space.3")} 0;
    color: ${getTheme("colors.secondaryFont")};

    .debug {
        color: ${getTheme("colors.textOrange")};
        text-align: center;
        font-size: ${getTheme("fontSizes.small")};
    }

    .internal {
        color: ${getTheme("colors.textOrange")};
    }

    .disclaimer {
        width: 100%;
        text-align: center;
        margin-top: ${getTheme("space.3")};
        padding-top: ${getTheme("space.3")};
        border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    ul {
        padding: 0;
        list-style: none;

        li {
            font-size: ${getTheme("fontSizes.small")};
            padding: ${getTheme("space.0")} 0;
            display: flex;
            align-items: center;

            a:hover {
                color: white;
            }
        }
    }

    .footer-icon {
        display: inline-flex;
        margin-right: ${getTheme("space.2")};
    }

    @media (max-width: ${getTheme("breakpoints.1")}) {
        text-align: center;

        .disclaimer {
            border: none;
        }

        li {
            justify-content: center;
        }

        ul::after {
            content: '';
            height: 1px;
            width: 90%;
            margin: ${getTheme("space.2")} auto;
            display: block;
            background: linear-gradient(90deg, rgba(2, 0, 36, 0) 0%, ${getTheme("colors.secondaryFont")} 50%, rgba(0, 0, 0, 0) 100%);
        }
    }

`


const Footer = () => {
  const router = useRouter()
  const {clientCtx} = useContext(ClientContext);
  const messagesQuery = useMessages()
  const footerDataQuery = useFooterData()
  const benefitsQuery = useBenefits()

  return (
    <FooterContain>
      {footerDataQuery.data && <>
        <FooterUpper>
          <div className="appContainer">
            <div className="columns">
              {footerDataQuery.data.paymentMethods && <div className="column-contain">
                {messagesQuery.data && <p>{messagesQuery.data.messages["footer-payment-options"]}</p>}
                <div className='payment-options'>
                  {footerDataQuery.data.paymentMethods.map((option: IFooterPaymentMethod) => {
                    return (
                      <div key={option.optionID} className="payment-option">
                        <img src={option.icon} alt={option.alternateTextKey}/>
                      </div>
                    )
                  })
                  }
                </div>
              </div>}

              {benefitsQuery.data && (benefitsQuery.data.length > 0) &&
                <div className="column-contain">
                  {messagesQuery.data && <p>{messagesQuery.data.messages["your-benefits"]}</p>}
                  <ul className="benefits">
                    {benefitsQuery.data.map((benefit: IBenefit) => {
                      return <li key={benefit.id}>{benefit.text}</li>
                    })}
                  </ul>
                </div>
              }

              <div className="column-contain">
                {messagesQuery.data &&
                  <p className="pb-1">{messagesQuery.data.messages["follow-us-on-social-media"]}</p>}

                {footerDataQuery.data.socials &&
                  <>
                    {footerDataQuery.data.socials.facebookLink &&
                      <a href={footerDataQuery.data.socials.facebookLink} target="_blank" rel="noreferrer"><
                        Icon variant={'large'} className="footer-icon"
                             color={baseTheme.colors.secondaryFont}>facebook-circle-line</Icon>
                      </a>}

                    {footerDataQuery.data.socials.instagramLink &&
                      <a href={footerDataQuery.data.socials.instagramLink} target="_blank" rel="noreferrer"><Icon
                        variant={'large'} className="footer-icon"
                        color={baseTheme.colors.secondaryFont}>instagram-line</Icon>
                      </a>}

                    {footerDataQuery.data.socials.twitterLink &&
                      <a href={footerDataQuery.data.socials.twitterLink} target="_blank" rel="noreferrer"><Icon
                        variant={'large'} className="footer-icon"
                        color={baseTheme.colors.secondaryFont}>twitter-logo</Icon>
                      </a>}

                    {footerDataQuery.data.socials.youtubeLink &&
                      <a href={footerDataQuery.data.socials.youtubeLink} target="_blank" rel="noreferrer"><Icon
                        variant={'large'} className="footer-icon"
                        color={baseTheme.colors.secondaryFont}>youtube-line</Icon>
                      </a>}

                    {footerDataQuery.data.socials.googleReviewLink &&
                      <a href={footerDataQuery.data.socials.googleReviewLink} target="_blank" rel="noreferrer"><Icon
                        variant={'large'} className="footer-icon"
                        color={baseTheme.colors.secondaryFont}>google-line</Icon>
                      </a>}
                  </>
                }

                <NewsletterSubscribe/>
              </div>

            </div>
          </div>
        </FooterUpper>

        <FooterLower>
          <div className="appContainer">
            <Container fluid>
              <Row>
                {footerDataQuery.data.links && footerDataQuery.data.links.groups.map((column: IFooterColumn) => {
                  return (
                    <Col md={4} sm={12} key={column.title}>
                      <div className="column-title">{column.title}</div>
                      <ul>
                        {column.links.map((footerLink: IFooterLink) => {
                          return (
                            <li key={footerLink.title}>
                              <Link className={footerLink.internal ? "internal" : ""}
                                    href={getNavPath(router, footerLink.href)}>{footerLink.title}</Link>
                            </li>
                          )
                        })}
                      </ul>
                    </Col>
                  )
                })}

                <Col md={4} sm={12}>
                  <div className="column-title">Contact</div>
                  <ul>
                    {(footerDataQuery.data.contactInfo && footerDataQuery.data.contactInfo.phoneNumber) &&
                      <li>
                        <Icon variant={"medium"} className="footer-icon"
                              color={baseTheme.colors.secondaryFont}>phone-line</Icon>
                        <span>{footerDataQuery.data.contactInfo.phoneNumber}</span>
                      </li>
                    }
                    {(footerDataQuery.data.contactInfo && footerDataQuery.data.contactInfo.email) &&
                      <li>
                        <Icon variant={"medium"} className="footer-icon"
                              color={baseTheme.colors.secondaryFont}>mail-line</Icon>
                        <span>{footerDataQuery.data.contactInfo.email}</span>
                      </li>
                    }
                    {(footerDataQuery.data.contactInfo && footerDataQuery.data.contactInfo.address) &&
                      <li>
                        <Icon variant={"medium"} className="footer-icon"
                              color={baseTheme.colors.secondaryFont}>map-pin-line</Icon>
                        <span>{footerDataQuery.data.contactInfo.address}</span>
                      </li>
                    }
                  </ul>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div
                    className='disclaimer'>&copy; {clientCtx != null && clientCtx.platformName} {new Date().getFullYear()}</div>
                </Col>
              </Row>
            </Container>

            {clientCtx?.debugLine &&
              <div className="debug">
                {clientCtx?.debugLine}
              </div>
            }
          </div>
        </FooterLower>
      </>}
    </FooterContain>
  )
}

export default Footer;